import { API_BASE_URL } from "../config";
import type {
  BenSpecQuestionLibraryResponse,
  BenSpecQuestionRow,
  BenSpecQuestionSetLibraryItem,
} from "../types";

const apiUrl = `${API_BASE_URL}/api`;

export interface UiBenSpecQuestionSet {
  id: string;
  name: string;
  source: BenSpecQuestionSetLibraryItem["source"];
  version: number | null;
  snapshotHash: string;
  rows: BenSpecQuestionRow[];
}

export interface UiBenSpecQuestionLibrary {
  defaultSet: UiBenSpecQuestionSet;
  options: UiBenSpecQuestionSet[];
}

/** Fetch the resolved ben spec question library for the signed-in user. */
export async function fetchBenSpecQuestionLibrary(
  accessToken: string
): Promise<BenSpecQuestionLibraryResponse> {
  const response = await fetch(`${apiUrl}/benefit-specifications/question-library`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      detail
        ? `Failed to fetch ben spec question library (${response.status}): ${detail}`
        : `Failed to fetch ben spec question library (${response.status}).`
    );
  }

  return (await response.json()) as BenSpecQuestionLibraryResponse;
}

/** Convert backend library rows into the existing editor row shape. */
export function normaliseBenSpecQuestionLibrary(
  library: BenSpecQuestionLibraryResponse
): UiBenSpecQuestionLibrary {
  const defaultSet = normaliseQuestionSet(library.default_set);
  const published = (library.published_sets || []).map(normaliseQuestionSet);
  const recent = (library.recent_sets || []).map(normaliseQuestionSet);
  return {
    defaultSet,
    options: [defaultSet, ...published, ...recent],
  };
}

export function normaliseQuestionSet(item: BenSpecQuestionSetLibraryItem): UiBenSpecQuestionSet {
  const sortedRows = [...(item.rows || [])].sort((a, b) => {
    return (a.order ?? 100) - (b.order ?? 100);
  });
  return {
    id: item.id,
    name: item.name,
    source: item.source,
    version: item.version ?? null,
    snapshotHash: item.snapshot_hash,
    rows: sortedRows.map((row) => ({
      id: row.id,
      section: row.section || "",
      subSection: row.sub_section || "",
      subSubSection: row.sub_sub_section || "",
      questionLabel: row.question_label || "",
      question: row.question || "",
      headingPath: row.heading_path || [],
      guidance: row.guidance || null,
    })),
  };
}
